/**
 * Records by URL, for any module that routes its own records (ROLLOUT-ORDERS-SHELL.md §3 D12).
 *
 * The pilot's useQuoteRoute was copied once, for Customers (C0). Orders would have been the
 * third copy, so the shape is a factory here instead: useCustomerRoute and useOrderRoute are
 * both built on it. useQuoteRoute stays as it is (its tabs, layout and scroll memory are its own).
 *
 *   <root>                 the list (list state in the query, defaults omitted)
 *   <root>/:id             that record; the list query rides along
 *   <root>/:id/<segment>   a record's sub-route (Customers' `edit`, Orders' `amend`)
 *   <root>/new             the add form, when the module has one (`newMode`); otherwise the
 *                          path parses as the list and the module's alias rule sends it there
 *   anything else          the record with `invalidId` (problem 'invalid'): no request is made
 *
 * History (pilot decision 3): list -> record pushes; record -> record replaces, so Back always
 * returns to the list in one step. Keys in `pushKeys` push, every other list key replaces, and a
 * change to a key in `filterKeys` starts on page 1.
 *
 * closeRecord goes back to the list by a PUSH of the list query the record URL carries (never
 * the bare root): the list the record was opened from comes back with its search and page.
 *
 * Config:
 *   root          '/customers'
 *   listKeys      the keys that describe the list; any other key is dropped from record URLs
 *   parseList     (URLSearchParams, defaults) => the list object
 *   defaults      the built-in list defaults; `page` is always 1
 *   useDefaults   optional hook (arg) => defaults, for per-user defaults (Customers' C4b)
 *   pushKeys      list keys whose change pushes (default ['page'])
 *   filterKeys    list keys whose change resets the page (default: every key but page)
 *   subRoutes     { segment: mode }, e.g. { edit: 'edit' }
 *   newMode       the mode for <root>/new, or null when the module has no add form
 *   isValidId     (segment) => id or null (default: a positive integer)
 *   legacyRedirect (pathname, search) => { pathname, search, state? } | null: the module's
 *                 legacy inbound forms, rewritten once with replace
 *   defaultFor    optional (key, URLSearchParams, defaults) => the default a key is judged
 *                 against in the query it lands in, for a key whose default depends on another
 *                 (Orders' dir on sort); default: defaults[key]
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const positiveInt = (value) => {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return null;
  const n = parseInt(value, 10);
  return n > 0 ? n : null;
};

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Serialise a list-key value, or null when it is empty (page 1 counts as empty). */
function encode(key, value) {
  if (value === null || value === undefined || value === '') return null;
  if (key === 'page') return Number(value) > 1 ? String(Number(value)) : null;
  return String(value);
}

const defaultFromDefaults = (key, sp, defaults) => (defaults && key in defaults ? defaults[key] : undefined);

const noDefaultsHook = () => null;

export function createRecordRoute(config) {
  const {
    root,
    listKeys,
    parseList,
    defaults: builtInDefaults,
    useDefaults = noDefaultsHook,
    pushKeys = ['page'],
    filterKeys = listKeys.filter((k) => k !== 'page'),
    subRoutes = {},
    newMode = null,
    isValidId = positiveInt,
    legacyRedirect = () => null,
    defaultFor = defaultFromDefaults
  } = config;

  const rootRe = new RegExp(`^${escapeRe(root)}/?`);

  /**
   * Pure parse of a location under `root`.
   * @returns {{ mode, id, invalidId, problem, list }}
   */
  function parse(pathname, search, defaults = builtInDefaults) {
    const rest = String(pathname || '').replace(rootRe, '').replace(/\/+$/, '');
    const segments = rest ? rest.split('/') : [];

    let mode = 'list';
    let id = null;
    let invalidId = false;
    if (segments.length === 1 && segments[0] === 'new') {
      // No add form: the path is the list until the module's alias rule rewrites it.
      mode = newMode || 'list';
    } else if (segments.length >= 1) {
      const parsed = isValidId(segments[0]);
      const subMode = segments.length === 2 ? subRoutes[segments[1]] : null;
      if (segments.length === 1 && parsed) {
        mode = 'record';
        id = parsed;
      } else if (subMode && parsed) {
        mode = subMode;
        id = parsed;
      } else {
        mode = 'record';
        invalidId = true;
      }
    }

    return {
      mode,
      id,
      invalidId,
      problem: invalidId ? 'invalid' : null,
      list: parseList(new URLSearchParams(search || ''), defaults || builtInDefaults)
    };
  }

  function useRecordRoute(arg) {
    const location = useLocation();
    const navigate = useNavigate();
    const userDefaults = useDefaults(arg);
    const defaults = userDefaults || builtInDefaults;

    // The newest search string, updated synchronously by every writer, so two writes in one
    // tick (Clear All, say) both land.
    const searchRef = useRef(location.search);
    searchRef.current = location.search;

    // When the defaults change (a preferences document arrives) the parse re-reads them even
    // if the URL stays the same.
    const parsed = useMemo(
      () => parse(location.pathname, location.search, defaults),
      [location.pathname, location.search, defaults]
    );

    // Legacy forms: rewritten once, with replace, so they never enter history.
    useEffect(() => {
      const to = legacyRedirect(location.pathname, location.search);
      if (to) {
        const { state, ...target } = to;
        navigate(target, { replace: true, state });
      }
    }, [location.pathname, location.search, navigate]);

    /** The list query to carry onto the next URL: list keys only. */
    const listQuery = useCallback(() => {
      const sp = new URLSearchParams(searchRef.current);
      for (const key of [...sp.keys()]) {
        if (!listKeys.includes(key)) sp.delete(key);
      }
      const qs = sp.toString();
      return qs ? `?${qs}` : '';
    }, []);

    const go = useCallback((pathname, search, replace) => {
      searchRef.current = search;
      navigate({ pathname, search }, { replace });
    }, [navigate]);

    /**
     * Open a record. Record -> record replaces (Back returns to the list in one step); so does
     * `{ replace: true }`, which a sub-route's Save and Cancel use to swap their entry for the
     * record they came from.
     */
    const openRecord = useCallback((id, { replace } = {}) => {
      go(`${root}/${id}`, listQuery(), replace ?? parsed.mode === 'record');
    }, [go, listQuery, parsed.mode]);

    /** Back to the list the record was opened from: its query, by push unless asked. */
    const closeRecord = useCallback(({ replace = false } = {}) => {
      go(root, listQuery(), replace);
    }, [go, listQuery]);

    /** A record's sub-route (edit, amend), pushed. */
    const openSubRoute = useCallback((id, segment) => {
      go(`${root}/${id}/${segment}`, listQuery(), false);
    }, [go, listQuery]);

    const openNew = useCallback(() => {
      go(`${root}/new`, listQuery(), false);
    }, [go, listQuery]);

    /**
     * Patch the list query. `null` / default values leave the URL. Replaces unless the patch
     * touches a push key, or `{ push: true }` is passed. A different filter starts on page 1
     * unless the patch names its own page. The path is kept: only the query changes.
     */
    const setList = useCallback((patch, { push } = {}) => {
      const sp = new URLSearchParams(searchRef.current);
      const entries = Object.entries(patch);
      for (const [key, value] of entries) {
        const encoded = encode(key, value);
        if (encoded === null) sp.delete(key);
        else sp.set(key, encoded);
      }
      // Defaults leave the URL, each judged against the query it lands in, once the whole
      // patch is in (a key's default may depend on another key in the same patch).
      for (const [key] of entries) {
        if (key === 'page' || !sp.has(key)) continue;
        const fallback = defaultFor(key, sp, defaults);
        if (fallback !== undefined && fallback !== null && String(fallback) === sp.get(key)) sp.delete(key);
      }
      if (!('page' in patch) && Object.keys(patch).some((k) => filterKeys.includes(k))) {
        sp.delete('page');
      }
      const qs = sp.toString();
      const search = qs ? `?${qs}` : '';
      const shouldPush = push ?? Object.keys(patch).some((k) => pushKeys.includes(k));
      if (search === location.search) return;
      go(location.pathname, search, !shouldPush);
    }, [go, location.pathname, location.search, defaults]);

    return useMemo(() => ({
      ...parsed,
      defaults,
      openRecord,
      closeRecord,
      openSubRoute,
      openNew,
      setList
    }), [parsed, defaults, openRecord, closeRecord, openSubRoute, openNew, setList]);
  }

  return { root, parse, useRecordRoute };
}
